/**
 * <selling-plans-alt> — subscription vs one-time UI, prices, variant rows.
 *
 * Dynamic pricing: each `refreshUI()` → `fetch(products/{handle}.js)` so allocations
 * match the current variant + selected delivery (UI plan id → variant-specific selling_plan_id).
 *
 * Headers: `[data-onetime-price]` is always variant one-time; `[data-subscription-price]` always uses
 * the selected delivery plan — independent of whether subscription or one-time purchase is selected.
 */
if (!customElements.get('selling-plans-alt')) {
  const SELECTORS = Object.freeze({
    radioInput: '.selling-plans-alt__radio-input',
    option: '.selling-plans-alt__option',
    optionSubscription: '.selling-plans-alt__option--subscription',
    optionOnetime: '.selling-plans-alt__option--onetime',
    delivery: '.selling-plans-alt__delivery',
    variants: '.selling-plans-alt__variants',
    variantsOnetime: '.selling-plans-alt__variants--onetime',
    variantInput: '.selling-plans-alt__variant-input',
    deliveryInput: '.selling-plans-alt__delivery-input',
  });

  const CENTS_EPSILON = 2;
  const SPURIOUS_EPSILON = 5;

  // ---------------------------------------------------------------------------
  // Debug
  // ---------------------------------------------------------------------------

  function splDebug(...args) {
    try {
      if (typeof window === 'undefined') return;
      const on =
        new URLSearchParams(window.location.search).get('spl_debug') === '1' ||
        window.localStorage?.getItem('spl_debug') === '1';
      if (on) console.info('[selling-plans-alt]', ...args);
    } catch {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------------------
  // Money & percentages (pure)
  // ---------------------------------------------------------------------------

  /**
   * Currency punctuation for the current market, derived from `data-money-sample` —
   * 1.234.567,89 rendered by the same Liquid filter the price nodes use. Deriving beats
   * `Intl`: the page locale is English while the market's currency keeps its own
   * separators, so Intl writes "€66.95" beside Liquid's "€66,95" on the same page.
   */
  let moneyShapeCache = null;
  function moneyShape() {
    if (moneyShapeCache) return moneyShapeCache;
    const sample = document.querySelector('[data-money-sample]')?.getAttribute('data-money-sample') || '';
    const digits = sample.match(/\d[\d.,\u00A0\u202F ]*\d/);
    if (!digits) return { prefix: '$', suffix: '', group: ',', decimal: '.', decimals: 2 };

    const raw = digits[0];
    // Only a separator followed by exactly two trailing digits is a decimal point; a
    // zero-decimal currency ("1.234.568 Ft") must not have its last group read as cents.
    const dec = raw.match(/([.,])(\d{2})$/);
    const intPart = dec ? raw.slice(0, -3) : raw;
    moneyShapeCache = {
      prefix: sample.slice(0, digits.index),
      suffix: sample.slice(digits.index + raw.length),
      group: (intPart.match(/[.,\u00A0\u202F ]/) || [''])[0],
      decimal: dec ? dec[1] : '',
      decimals: dec ? 2 : 0,
    };
    return moneyShapeCache;
  }

  const Money = {
    /**
     * Identity. Prices are displayed to the cent: this used to round to whole units, which
     * is a no-op in USD (every shop-currency price is whole) but in every converted market
     * printed a price nobody is charged — €66,95 in the cart against "€67" in the buy box.
     */
    displayCents(cents) {
      return Math.round(Number(cents) || 0);
    },

    percentOffCompare(compareCents, saleCents) {
      const compare = Number(compareCents) || 0;
      const sale = Number(saleCents) || 0;
      if (compare <= sale || compare <= 0) return 0;
      return Math.round(((compare - sale) / compare) * 100);
    },

    /** Matches Liquid's `money_without_trailing_zeros`, so SSR and repaint agree. */
    formatDisplay(cents) {
      // Shared with norse-buy-box, which writes the add-to-cart label on the same page —
      // two formatters meant the button and the price row disagreed. The local copy below
      // stays as the fallback: these are separately cached assets.
      const shared = window.NorseSellingPlanUtils?.formatMoney;
      if (shared) return shared(cents);

      const shape = moneyShape();
      const n = Math.abs(Money.displayCents(cents));
      const whole = shape.decimals === 2 ? Math.floor(n / 100) : Math.round(n / 100);
      const fraction = shape.decimals === 2 ? String(n % 100).padStart(2, '0') : '';

      let out = String(whole);
      if (shape.group) out = out.replace(/\B(?=(\d{3})+(?!\d))/g, shape.group);
      if (fraction && fraction !== '00') out += shape.decimal + fraction;
      return (cents < 0 ? '-' : '') + shape.prefix + out + shape.suffix;
    },
  };

  // ---------------------------------------------------------------------------
  // Variant / kit helpers (pure, match Liquid)
  // ---------------------------------------------------------------------------

  function parseKitCountFromVariant(variant) {
    const title = String(variant?.title || '').trim();
    const first = parseInt(title.split(/\s+/)[0], 10);
    if (Number.isFinite(first) && first >= 1 && first <= 48) return first;
    const min = variant?.quantity_rule?.min;
    if (min != null && Number(min) >= 1) return Number(min);
    return 1;
  }

  function findOnePackVariant(product) {
    return product?.variants?.find(v => parseKitCountFromVariant(v) === 1) ?? null;
  }

  function getReferenceComparePerRitualCents(product) {
    const v = findOnePackVariant(product);
    return v ? v.compare_at_price || v.price || 0 : 0;
  }

  /** Line compare for headers + variant-row strikethrough (1-pack MSRP × kit count). */
  function getLineCompareCentsForHeader(product, variant) {
    const kit = parseKitCountFromVariant(variant);
    const ref = getReferenceComparePerRitualCents(product);
    if (ref > 0 && kit >= 1) return ref * kit;
    return variant.compare_at_price || variant.price || 0;
  }

  /** Cart-drawer compare priority on top of ritual bundle line compare (1-pack MSRP × kit). */
  function getCartAlignedCompareCents(product, variant, saleLineCents, isSubscription) {
    const sale = Number(saleLineCents) || 0;
    const oneTime = Number(variant.price) || 0;
    let compareAt = getLineCompareCentsForHeader(product, variant);
    if (compareAt > sale) return compareAt;
    if (isSubscription && oneTime > sale) return oneTime;
    return compareAt || oneTime;
  }

  // ---------------------------------------------------------------------------
  // Shopify product JSON — selling plans & allocations (pure)
  // ---------------------------------------------------------------------------

  const ProductPlans = {
    findSellingPlanById(product, planIdN) {
      if (!planIdN) return null;
      return product.selling_plan_groups
        ?.flatMap(g => g.selling_plans || [])
        .find(plan => Number(plan.id) === Number(planIdN));
    },

    findAllocationDirect(variant, planIdN) {
      const allocations = variant.selling_plan_allocations || [];
      return allocations.find(
        a =>
          Number(a.selling_plan_id) === Number(planIdN) ||
          Number(a.selling_plan?.id) === Number(planIdN)
      );
    },

    /**
     * UI uses plan ids from selling_plan_groups[0]; bundle SKUs sell through other
     * groups with different ids. Shared resolution (direct id → ~40-day name match
     * → slot-index fallback) lives in norse-selling-plan-utils.js.
     */
    resolveAllocation(product, variant, uiPlanId) {
      if (!variant || uiPlanId == null || uiPlanId === '') return null;
      const planIdN = Number(uiPlanId);
      if (!Number.isFinite(planIdN)) return null;

      const utils = window.NorseSellingPlanUtils;
      if (utils) {
        const alloc = utils.resolveAllocation(product, variant, uiPlanId);
        if (alloc) {
          splDebug('resolveAllocation', {
            variantId: variant.id,
            uiPlanId: planIdN,
            resolvedSellingPlanId: alloc.selling_plan_id ?? alloc.selling_plan?.id,
          });
        }
        return alloc;
      }

      // Minimal fallback (utils script missing): direct id match only
      return ProductPlans.findAllocationDirect(variant, planIdN);
    },

    canonicalSellingPlanId(product, variant, uiPlanId) {
      const planIdN = Number(uiPlanId);
      if (!Number.isFinite(planIdN)) return null;
      const alloc = ProductPlans.resolveAllocation(product, variant, uiPlanId);
      const sid = alloc?.selling_plan_id ?? alloc?.selling_plan?.id;
      if (sid != null && !Number.isNaN(Number(sid))) return Number(sid);
      return planIdN;
    },
  };

  // ---------------------------------------------------------------------------
  // Subscription line resolution (pure math on JSON + optional DOM cents)
  // ---------------------------------------------------------------------------

  function isSpuriousBundleAllocation(allocPriceCents, kit, onePackSubCents) {
    if (kit <= 1) return false;
    if (onePackSubCents == null || Number.isNaN(Number(onePackSubCents))) return false;
    return Math.abs(Number(allocPriceCents) - Number(onePackSubCents)) <= SPURIOUS_EPSILON;
  }

  function pickDiscountedLineFromAllocation(allocation, variantPriceCents, kit, onePackSubCents) {
    if (!allocation) return null;
    const vp = Number(variantPriceCents) || 0;

    const tryVal = (label, centsRaw) => {
      const c = Number(centsRaw);
      if (!Number.isFinite(c) || c <= 0) return null;
      if (c >= vp - CENTS_EPSILON) return null;
      if (isSpuriousBundleAllocation(c, kit, onePackSubCents)) return null;
      if (label !== 'price') {
        splDebug('allocation field used for subscription line', { field: label, cents: c, variantPrice: vp });
      }
      return c;
    };

    let out = tryVal('price', allocation.price);
    if (out != null) return out;
    if (allocation.checkout_charge_amount != null) {
      out = tryVal('checkout_charge_amount', allocation.checkout_charge_amount);
      if (out != null) return out;
    }
    return null;
  }

  function applySellingPlanAdjustments(variantPriceCents, sellingPlan, kit) {
    let priceCents = Number(variantPriceCents) || 0;
    if (!sellingPlan?.price_adjustments?.length) return priceCents;
    const k = Math.max(1, kit);

    for (const adjustment of sellingPlan.price_adjustments) {
      const val = Number(adjustment.value);
      if (Number.isNaN(val)) continue;
      if (adjustment.value_type === 'percentage') {
        priceCents -= (priceCents * val) / 100;
      } else if (adjustment.value_type === 'fixed_amount') {
        priceCents -= val;
      } else if (adjustment.value_type === 'price') {
        if (k <= 1) {
          priceCents = val;
          break;
        }
      }
    }
    return Math.max(0, priceCents);
  }

  function subscriptionLineForReferenceVariant(variant, product, planIdN, sellingPlan) {
    const vp = Number(variant.price) || 0;
    const kit = parseKitCountFromVariant(variant);
    const alloc = ProductPlans.resolveAllocation(product, variant, planIdN);
    const fromAlloc = pickDiscountedLineFromAllocation(alloc, vp, kit, null);
    if (fromAlloc != null) return fromAlloc;
    const adj = applySellingPlanAdjustments(vp, sellingPlan, kit);
    if (adj < vp - CENTS_EPSILON) return adj;
    return null;
  }

  function getOnePackSubscriptionLineCents(product, planIdN, sellingPlan) {
    const ref = findOnePackVariant(product);
    if (!ref) return null;
    return subscriptionLineForReferenceVariant(ref, product, planIdN, sellingPlan);
  }

  /**
   * @param {object} ctx
   * @param {() => number | null} ctx.readLiquidCents - SSR row `data-subscription-line-cents` when plan matches default
   * @param {(variantId: number, planId: number) => number | null} [ctx.readSsrAllocCents] - `data-ssr-alloc-cents`
   */
  function getSubscriptionLineCents(variant, product, planIdN, ctx) {
    if (!planIdN) return variant.price;
    const liquid = ctx?.readLiquidCents;

    const vp = Number(variant.price) || 0;
    const sellingPlan = ProductPlans.findSellingPlanById(product, planIdN);
    const kit = parseKitCountFromVariant(variant);
    const onePackSub = getOnePackSubscriptionLineCents(product, planIdN, sellingPlan);

    const fromLiquid = liquid?.() ?? null;
    if (fromLiquid != null && fromLiquid > 0 && fromLiquid < vp - CENTS_EPSILON) {
      splDebug('subscription line from Liquid DOM', {
        variantId: variant.id,
        planId: planIdN,
        cents: fromLiquid,
        oneTimeCents: vp,
      });
      return fromLiquid;
    }
    if (fromLiquid != null && fromLiquid >= vp - CENTS_EPSILON) {
      splDebug('ignoring Liquid DOM (same as one-time); using product.js', {
        variantId: variant.id,
        planId: planIdN,
        fromLiquid,
        oneTimeCents: vp,
        kit,
      });
    }

    const allocation = ProductPlans.resolveAllocation(product, variant, planIdN);

    // products/{handle}.js prices a fixed-price plan adjustment as a ratio of the LOCAL
    // variant price; the cart converts the plan's own price. In USD (rate 1.0) the two
    // agree, so this only ever shows up in a converted market — and it showed up as €68,95
    // against the €66,95 actually charged. Liquid priced the same allocation the way the
    // cart does, so its cents win whenever we have them.
    const allocPlanId = allocation?.selling_plan_id ?? allocation?.selling_plan?.id ?? planIdN;
    const ssrCents = ctx?.readSsrAllocCents?.(variant.id, allocPlanId) ?? null;
    if (ssrCents != null && ssrCents !== Number(allocation?.price)) {
      splDebug('allocation cents from Liquid, overriding product.js', {
        variantId: variant.id,
        planId: allocPlanId,
        liquid: ssrCents,
        productJs: allocation?.price,
      });
    }

    const pricedAllocation =
      ssrCents != null && allocation ? { ...allocation, price: ssrCents } : allocation;
    const fromAlloc = pickDiscountedLineFromAllocation(pricedAllocation, vp, kit, onePackSub);
    if (fromAlloc != null) return fromAlloc;

    const fromAdjust = applySellingPlanAdjustments(vp, sellingPlan, kit);
    if (fromAdjust < vp - CENTS_EPSILON) return fromAdjust;

    return vp;
  }

  function computeVariantRowSavePercent(product, variant, planIdNum, isSubscriptionColumn, liquidCtx) {
    if (isSubscriptionColumn) {
      const sub = getSubscriptionLineCents(variant, product, planIdNum, liquidCtx);
      const subCents = Money.displayCents(sub);
      const lineCmp = getCartAlignedCompareCents(product, variant, subCents, true);
      let pct = Money.percentOffCompare(lineCmp, subCents);
      if (pct === 0 && variant.price > subCents) {
        pct = Math.round(((variant.price - subCents) / variant.price) * 100);
      }
      return pct;
    }

    const otCents = Money.displayCents(variant.price);
    const lineCmp = getCartAlignedCompareCents(product, variant, otCents, false);
    return Money.percentOffCompare(lineCmp, otCents);
  }

  function computePlanBadgePercents(product, variant, onetimeLineCents, subscriptionLineCents) {
    const otCompare = getCartAlignedCompareCents(product, variant, onetimeLineCents, false);
    const subCompare = getCartAlignedCompareCents(product, variant, subscriptionLineCents, true);

    const onetimePct = Money.percentOffCompare(otCompare, onetimeLineCents);
    const subPct = Money.percentOffCompare(subCompare, subscriptionLineCents);

    return { onetimePct, subPct };
  }

  // ---------------------------------------------------------------------------
  // DOM iteration (DRY)
  // ---------------------------------------------------------------------------

  function forEachKitVariantRow(host, optionSelector, product, fn) {
    host.querySelectorAll(`${optionSelector} .selling-plans-alt__variant-option`).forEach(row => {
      const inp = row.querySelector(SELECTORS.variantInput);
      const vid = parseInt(inp?.dataset.variantId || inp?.value, 10);
      if (!Number.isFinite(vid)) return;
      const variant = product.variants.find(x => x.id === vid);
      if (!variant) return;
      fn({ row, variant, inp, vid });
    });
  }

  // ---------------------------------------------------------------------------
  // Custom element — orchestration only
  // ---------------------------------------------------------------------------

  customElements.define(
    'selling-plans-alt',
    class SellingPlansAlt extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productHandle = this.dataset.productHandle;
        this._refreshLock = Promise.resolve();
        this._priceUpdateRunId = 0;
      }

      connectedCallback() {
        void this.init();
      }

      getForm() {
        return document.getElementById(`product-form-${this.sectionId}`);
      }

      /** Same product section only — avoids coupling to unrelated NBB elsewhere on the page. */
      _pairedNorseBuyBox() {
        const form = this.getForm();
        const root = form?.closest('product-info') || document;
        return root.querySelector('norse-buy-box');
      }

      /** When subscription is chosen in Norse Buy Box — delivery radios live there only. */
      _nbbSellingPlanUiId() {
        const nbb = this._pairedNorseBuyBox();
        if (!nbb?.isSubscriptionSelected) return '';
        const opt = nbb.querySelector('.nbb__delivery-option.is-selected');
        return opt?.dataset?.sellingPlanId ? String(opt.dataset.sellingPlanId) : '';
      }

      getVariantId() {
        return this.getSelectedVariantId();
      }

      getVariantContainers() {
        const subscriptionOption = this.querySelector(SELECTORS.optionSubscription);
        let subscriptionVariants = subscriptionOption?.querySelector(
          `${SELECTORS.variants}:not(${SELECTORS.variantsOnetime})`
        );
        if (!subscriptionVariants) {
          subscriptionVariants = this.querySelector(
            `:scope > ${SELECTORS.variants}:not(${SELECTORS.variantsOnetime})`
          );
        }
        const onetimeOption = this.querySelector(SELECTORS.optionOnetime);
        let onetimeVariants = onetimeOption?.querySelector(SELECTORS.variantsOnetime);
        if (!onetimeVariants) {
          onetimeVariants = this.querySelector(`:scope > ${SELECTORS.variantsOnetime}`);
        }
        return { subscriptionVariants, onetimeVariants };
      }

      resolveSellingPlanIdFromUi(checkedPlanRadio) {
        if (!checkedPlanRadio?.value) return '';
        const deliveryChecked = this.querySelector(`${SELECTORS.deliveryInput}:checked`);
        return (
          deliveryChecked?.dataset.sellingPlanId ||
          checkedPlanRadio.dataset.sellingPlanId ||
          checkedPlanRadio.value ||
          ''
        );
      }

      /** Resolves UI selling plan id (delivery > radio) for price rows + fetch. */
      resolveUiSellingPlanId() {
        if (this.dataset.force60PlanId) return this.dataset.force60PlanId;

        const nbbUi = this._nbbSellingPlanUiId();
        if (nbbUi) return nbbUi;

        const checkedPlanRadio = this.querySelector(`${SELECTORS.radioInput}:checked`);
        let id = '';
        if (checkedPlanRadio?.value) {
          id = this.resolveSellingPlanIdFromUi(checkedPlanRadio);
        }
        if (!id && checkedPlanRadio?.value) {
          id =
            this.querySelector(SELECTORS.deliveryInput)?.dataset?.sellingPlanId ||
            this.querySelector(`${SELECTORS.optionSubscription} ${SELECTORS.radioInput}`)?.dataset
              ?.sellingPlanId ||
            '';
        }
        return id;
      }

      /**
       * Plan id from delivery UI (1-pack group ids) — used for subscription line math for every variant.
       * Independent of subscription vs one-time toggle so header `[data-subscription-price]` stays correct.
       */
      resolvePlanIdForVariantRows(product) {
        if (this.dataset.force60PlanId) {
          return parseInt(this.dataset.force60PlanId, 10) || 0;
        }

        const nbbUi = this._nbbSellingPlanUiId();
        if (nbbUi) return parseInt(nbbUi, 10) || 0;

        const checked = this.querySelector(`${SELECTORS.deliveryInput}:checked`);
        const raw =
          checked?.dataset?.sellingPlanId ||
          this.querySelector(SELECTORS.deliveryInput)?.dataset?.sellingPlanId ||
          product.selling_plan_groups?.[0]?.selling_plans?.[0]?.id ||
          0;
        return parseInt(String(raw), 10) || 0;
      }

      /** Subscription totals always use delivery frequency; never the one-time line. */
      resolveSubscriptionPlanIdForHeaders(product) {
        return (
          this.resolvePlanIdForVariantRows(product) ||
          parseInt(String(product.selling_plan_groups?.[0]?.selling_plans?.[0]?.id || '0'), 10) ||
          0
        );
      }

      refreshUI() {
        if (this.classList.contains('style-5')) return Promise.resolve();
        this._refreshLock = this._refreshLock
          .catch(() => {})
          .then(() => this._runRefreshUI());
        return this._refreshLock;
      }

      async _runRefreshUI() {
        const activeVariantId = this.getActiveVariantIdForPricing();
        if (activeVariantId) {
          const nbb = this._pairedNorseBuyBox();
          if (nbb) this.syncAllVariantRadios(activeVariantId);
          else this.reconcileFormVariantId(activeVariantId);
        }
        await this.updatePrices();
        this.updateButtonText();
      }

      getActiveVariantIdForPricing() {
        const nbb = this._pairedNorseBuyBox();
        const form = this.getForm();
        const byForm = form?.querySelector('input[name="id"]')?.value;

        /* NBB owns pack + plan; SPL rows are visual echo only — never let stale SPL radios trump the form. */
        if (nbb && byForm) return String(byForm);
        if (nbb) {
          const card = nbb.querySelector('.nbb__variant-option.is-selected');
          if (card?.dataset.variantId) return String(card.dataset.variantId);
        }

        const checkedPlan = this.querySelector(`${SELECTORS.radioInput}:checked`);
        const subscriptionSelected = Boolean(checkedPlan?.value);

        const subVariant = this.querySelector(
          `${SELECTORS.optionSubscription} ${SELECTORS.variantInput}:checked`
        );
        const otVariant = this.querySelector(`${SELECTORS.optionOnetime} ${SELECTORS.variantInput}:checked`);

        if (subscriptionSelected && subVariant) {
          return String(subVariant.dataset.variantId || subVariant.value || '');
        }
        if (!subscriptionSelected && otVariant) {
          return String(otVariant.dataset.variantId || otVariant.value || '');
        }

        const anyChecked = this.querySelector(`${SELECTORS.variantInput}:checked`);
        if (anyChecked) {
          return String(anyChecked.dataset.variantId || anyChecked.value || '');
        }

        return this.getSelectedVariantId();
      }

      reconcileFormVariantId(variantId) {
        if (!variantId) return;
        const form = this.getForm();
        const idInput = form?.querySelector('input[name="id"]');
        if (!idInput || String(idInput.value) === String(variantId)) {
          this.syncAllVariantRadios(variantId);
          return;
        }
        idInput.value = variantId;
        this.syncAllVariantRadios(variantId);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        this._emitVariantChange(variantId);
      }

      _emitVariantChange(variantId) {
        document.dispatchEvent(
          new CustomEvent('variant:change', { detail: { variantId, source: 'selling-plans-alt' } })
        );
      }

      async init() {
        const onPlanChange = input => {
          const planId = this.resolveSellingPlanIdFromUi(input);
          this.updateSellingPlan(planId);
          void this.refreshUI();
        };

        this.querySelectorAll(SELECTORS.radioInput).forEach(input => {
          input.addEventListener('change', () => onPlanChange(input));
        });

        this.querySelectorAll(SELECTORS.option).forEach(option => {
          option.addEventListener('click', e => {
            if (e.target.closest(SELECTORS.delivery) || e.target.closest(SELECTORS.variants)) return;
            const radio = option.querySelector(SELECTORS.radioInput);
            if (radio && !radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });

        this.querySelectorAll(SELECTORS.delivery).forEach(el => {
          el.addEventListener('click', e => e.stopPropagation());
        });
        this.querySelectorAll(SELECTORS.variants).forEach(el => {
          el.addEventListener('click', e => e.stopPropagation());
        });

        this.querySelectorAll(SELECTORS.variantInput).forEach(input => {
          input.addEventListener('change', () => {
            if (!input.checked) return;
            const variantId = input.dataset.variantId || input.value;
            this.syncAllVariantRadios(variantId);
            const form = this.getForm();
            const idInput = form?.querySelector('input[name="id"]');
            if (idInput) {
              idInput.value = variantId;
              idInput.dispatchEvent(new Event('change', { bubbles: true }));
              this._emitVariantChange(variantId);
            }
            void this.refreshUI();
          });
        });
        
        this.querySelectorAll(SELECTORS.deliveryInput).forEach(input => {
          input.addEventListener('change', () => {
            if (!input.checked) return;
            const subscriptionRadio = this.querySelector(
              `${SELECTORS.optionSubscription} ${SELECTORS.radioInput}`
            );
              if (subscriptionRadio) {
                subscriptionRadio.checked = true;
              subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
        
        const boundVariantChange = ev => {
          const variantId = ev.detail?.variantId || this.getVariantId();
          if (variantId) this.syncAllVariantRadios(variantId);
          if (ev.detail?.source === 'selling-plans-alt') return;
          void this.refreshUI();
        };
        document.addEventListener('variant:change', boundVariantChange);

        this.updateInitialSelection();
        await this.refreshUI();
      }
      
      /**
       * When the cart form uses a variant id that has no kit row (e.g. unavailable URL variant),
       * no subscription/onetime radio is checked — sync by selecting the first visible kit option.
       */
      ensureKitVariantSelected() {
        const checkedPlan = this.querySelector(`${SELECTORS.radioInput}:checked`);
        const subscriptionSelected = Boolean(checkedPlan?.value);
        const { subscriptionVariants, onetimeVariants } = this.getVariantContainers();
        const style2Shared = this.classList.contains('style-2')
          ? this.querySelector(`:scope > ${SELECTORS.variants}:not(${SELECTORS.variantsOnetime})`)
          : null;

        let container = null;
        if (this.classList.contains('style-2') && style2Shared) {
          container = style2Shared;
        } else {
          container = subscriptionSelected ? subscriptionVariants : onetimeVariants;
        }

        const checked = container?.querySelector(`${SELECTORS.variantInput}:checked`);
        if (checked) {
          this.syncAllVariantRadios(checked.dataset.variantId || checked.value);
          return;
        }

        const first = container?.querySelector(SELECTORS.variantInput);
        if (first) {
          first.checked = true;
          first.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        this.syncAllVariantRadios(this.getSelectedVariantId());
      }

      updateInitialSelection() {
        const checkedRadio = this.querySelector(`${SELECTORS.radioInput}:checked`);
        if (checkedRadio) {
          this.updateSellingPlan(this.resolveSellingPlanIdFromUi(checkedRadio));
        }
        this.ensureKitVariantSelected();
      }
      
      updateSellingPlan(planId) {
        const hiddenInput = this.querySelector(`#selling-plan-input-${this.sectionId}`);
        if (hiddenInput) {
          hiddenInput.value = planId || '';
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        const subscriptionOption = this.querySelector(SELECTORS.optionSubscription);
        const onetimeOption = this.querySelector(SELECTORS.optionOnetime);
        const delivery = this.querySelector(SELECTORS.delivery);
        const { subscriptionVariants, onetimeVariants } = this.getVariantContainers();
        const style2SharedVariants = this.classList.contains('style-2')
          ? this.querySelector(':scope > .selling-plans-alt__variants:not(.selling-plans-alt__variants--onetime)')
          : null;

        if (planId) {
          subscriptionOption?.classList.add('is-selected');
          onetimeOption?.classList.remove('is-selected');
          this.classList.add('is-subscription-selected');
          if (delivery) delivery.style.display = 'flex';
          if (style2SharedVariants) {
            style2SharedVariants.style.removeProperty('display');
          } else {
            if (subscriptionVariants) subscriptionVariants.style.display = '';
            if (onetimeVariants) onetimeVariants.style.display = 'none';
          }
        } else {
          subscriptionOption?.classList.remove('is-selected');
          onetimeOption?.classList.add('is-selected');
          this.classList.remove('is-subscription-selected');
          if (delivery) delivery.style.display = 'none';
          if (style2SharedVariants) {
            style2SharedVariants.style.removeProperty('display');
          } else {
            if (subscriptionVariants) subscriptionVariants.style.display = 'none';
            if (onetimeVariants) onetimeVariants.style.display = '';
          }
        }
        this.syncAllVariantRadios(this.getVariantId());
      }

      syncAllVariantRadios(variantId) {
        if (!variantId) return;
        const s = String(variantId);
        this.querySelectorAll(SELECTORS.variantInput).forEach(inp => {
          const vid = String(inp.dataset.variantId || inp.value);
          inp.checked = vid === s;
        });
      }

      getSelectedVariantId() {
        const planInput = this.querySelector(`#selling-plan-input-${this.sectionId}`);
        const formId = planInput?.getAttribute('form');
        const form = formId ? document.getElementById(formId) : this.closest('form');
        const byForm = form?.querySelector('input[name="id"]')?.value;
        if (byForm) return byForm;
        return document.querySelector('.product-variant-id')?.value || '';
      }

      /** Liquid SSR cents on subscription column row; only when selected plan === default plan in HTML. */
      _readLiquidSubscriptionLineCents(variantId, sellingPlanIdNum) {
        const defPlan = this.dataset.defaultSellingPlanId;
        if (defPlan == null || String(sellingPlanIdNum) !== String(defPlan)) return null;

        const sid = String(variantId);
        const subRoot = this.querySelector(SELECTORS.optionSubscription);
        const scope = subRoot || this;
        const inp = scope.querySelector(`.selling-plans-alt__variant-option input[data-variant-id="${sid}"]`);
        const row = inp?.closest('.selling-plans-alt__variant-option');
        const raw = row?.dataset?.subscriptionLineCents;
        if (raw == null || raw === '') return null;
        const cents = parseInt(raw, 10);
        if (!Number.isFinite(cents) || cents < 0) return null;
        return cents;
      }

      /**
       * Cart-true allocation cents for a (variant, plan) pair, rendered by Liquid onto the
       * host. Authoritative over products/{handle}.js — see getSubscriptionLineCents().
       */
      _readSsrAllocCents(variantId, planId) {
        if (this._ssrAllocCents === undefined) {
          try {
            this._ssrAllocCents = JSON.parse(this.dataset.ssrAllocCents || 'null');
          } catch {
            this._ssrAllocCents = null;
          }
        }
        const cents = this._ssrAllocCents?.[String(variantId)]?.[String(planId)];
        return Number.isFinite(cents) && cents > 0 ? cents : null;
      }

      syncCanonicalSellingPlanInput(product, variant, uiPlanIdNum) {
        if (!uiPlanIdNum || !variant) return;
        const canonical = ProductPlans.canonicalSellingPlanId(product, variant, uiPlanIdNum);
        if (canonical == null) return;
        const idStr = String(canonical);
        const hidden = this.querySelector(`#selling-plan-input-${this.sectionId}`);
        if (hidden) hidden.value = idStr;
        const subRadio = this.querySelector(`${SELECTORS.optionSubscription} ${SELECTORS.radioInput}`);
        if (subRadio?.checked) subRadio.value = idStr;
      }

      updateDiscountBadgesFromPercents(onetimePct, subPct) {
        this.querySelectorAll('[data-selling-plan-badge="onetime"]').forEach(el => {
          if (onetimePct > 0) {
            el.textContent = `SAVE ${onetimePct}%`;
            el.classList.remove('is-hidden');
          } else {
            el.textContent = '';
            el.classList.add('is-hidden');
          }
        });
        this.querySelectorAll('[data-selling-plan-badge="subscription"]').forEach(el => {
          if (subPct > 0) {
            el.textContent = `save ${subPct}% on your 1st order`;
            el.classList.remove('is-hidden');
          } else {
            el.textContent = '';
            el.classList.add('is-hidden');
          }
        });
      }

      updateVariantRowSaveBadges(product, planIdNum) {
        const plan = Number(planIdNum) || 0;
        const run = (optionSelector, isSub) => {
          forEachKitVariantRow(this, optionSelector, product, ({ row, variant }) => {
            const liquidCtx = {
              readLiquidCents: () => this._readLiquidSubscriptionLineCents(variant.id, plan),
              readSsrAllocCents: (vid, pid) => this._readSsrAllocCents(vid, pid),
            };
            const pct = computeVariantRowSavePercent(product, variant, plan, isSub, liquidCtx);
            const badge = row.querySelector('[data-variant-save-badge]');
            if (!badge) return;
            if (pct > 0) {
              badge.textContent = `Save ${pct}%`;
              badge.dataset.savePercent = String(pct);
              badge.hidden = false;
            } else {
              badge.textContent = '';
              badge.dataset.savePercent = '0';
              badge.hidden = true;
            }
          });
        };
        run(SELECTORS.optionSubscription, true);
        run(SELECTORS.optionOnetime, false);
      }

      updateVariantRowPriceDisplays(product, planIdNum) {
        const plan = Number(planIdNum) || 0;
        const fill = (optionSelector, isOnetimeColumn) => {
          forEachKitVariantRow(this, optionSelector, product, ({ row, variant }) => {
            const kit = Math.max(1, parseKitCountFromVariant(variant));
            const lineOt = Number(variant.price) || 0;
            const otPer = Money.displayCents(lineOt / kit);
            const liquidCtx = {
              readLiquidCents: () => this._readLiquidSubscriptionLineCents(variant.id, plan),
              readSsrAllocCents: (vid, pid) => this._readSsrAllocCents(vid, pid),
            };
            const lineSub = plan ? getSubscriptionLineCents(variant, product, plan, liquidCtx) : lineOt;
            const subPer = Money.displayCents(lineSub / kit);
            const lineCmp = getLineCompareCentsForHeader(product, variant);
            const cmpPer = Money.displayCents(lineCmp / kit);

            const otEl = row.querySelector('[data-variant-onetime-price]');
            const subEl = row.querySelector('[data-variant-subscription-price]');
            const cmpEl = row.querySelector('[data-variant-compare]');
            if (otEl) otEl.textContent = Money.formatDisplay(otPer);
            if (subEl) subEl.textContent = Money.formatDisplay(subPer);
            if (cmpEl) {
              cmpEl.textContent = Money.formatDisplay(cmpPer);
              const salePer = isOnetimeColumn ? otPer : subPer;
              const hideCompare = isOnetimeColumn || cmpPer <= salePer;
              cmpEl.classList.toggle('is-hidden', hideCompare);
            }
          });
        };
        fill(SELECTORS.optionSubscription, false);
        fill(SELECTORS.optionOnetime, true);
        if (this.classList.contains('style-2')) {
          fill(`:scope > ${SELECTORS.variants}:not(${SELECTORS.variantsOnetime})`, false);
        }
      }
      
      async updatePrices() {
        const runId = ++this._priceUpdateRunId;
        const selectedVariant = this.getActiveVariantIdForPricing();
        if (!selectedVariant || !this.productHandle) return;

        const sellingPlanId = this.resolveUiSellingPlanId();
          
          try {
            const response = await fetch(`${window.Shopify.routes.root}products/${this.productHandle}.js`);
            const product = await response.json();
          if (runId !== this._priceUpdateRunId) return;

          const variant = product.variants.find(v => v.id === parseInt(selectedVariant, 10));
          if (!variant) return;

          const lineCompareCents = getLineCompareCentsForHeader(product, variant);
          const compareDisplayCents = Money.displayCents(lineCompareCents);

          const onetimeLine = variant.price;
          const onetimeLineCents = Money.displayCents(onetimeLine);

          const onetimeEl = this.querySelector('[data-onetime-price]');
          if (onetimeEl) onetimeEl.textContent = Money.formatDisplay(onetimeLineCents);

          const sellingPlanIdNum = sellingPlanId ? parseInt(sellingPlanId, 10) : 0;

          const subPlanIdForHeaders = this.resolveSubscriptionPlanIdForHeaders(product);
          const readLiquidSub = () => this._readLiquidSubscriptionLineCents(variant.id, subPlanIdForHeaders);
          const subscriptionLine =
            subPlanIdForHeaders > 0
              ? getSubscriptionLineCents(variant, product, subPlanIdForHeaders, {
                  readLiquidCents: readLiquidSub,
                  readSsrAllocCents: (vid, pid) => this._readSsrAllocCents(vid, pid),
                })
              : onetimeLine;
          const subscriptionLineCents = Money.displayCents(subscriptionLine);

          const priceElement = this.querySelector('[data-subscription-price]');
          if (priceElement) priceElement.textContent = Money.formatDisplay(subscriptionLineCents);

          this.querySelectorAll('.selling-plans-alt__price-old').forEach(el => {
            el.textContent = Money.formatDisplay(compareDisplayCents);
          });

          const { onetimePct, subPct } = computePlanBadgePercents(
            product,
            variant,
            onetimeLineCents,
            subscriptionLineCents
          );
          this.updateDiscountBadgesFromPercents(onetimePct, subPct);

          if (runId !== this._priceUpdateRunId) return;

          const checkedStickyPlan = this.querySelector(`${SELECTORS.radioInput}:checked`);
          const subscriptionSelected = Boolean(checkedStickyPlan?.value);
          const stickyLineCents = subscriptionSelected ? subscriptionLineCents : onetimeLineCents;
          const stickySavePercent = subscriptionSelected ? subPct : onetimePct;

          document.dispatchEvent(
            new CustomEvent('selling-plans-alt:sticky-sync', {
              bubbles: true,
              detail: {
                productSectionId: this.sectionId,
                variantId: variant.id,
                variantAvailable: Boolean(variant.available),
                subSavePercent: stickySavePercent,
                subscriptionMoney: Money.formatDisplay(stickyLineCents),
                compareMoney: Money.formatDisplay(compareDisplayCents),
                subscriptionLineRoundedCents: stickyLineCents,
                compareDisplayCents,
                showCompareStrikethrough: compareDisplayCents > stickyLineCents,
              },
            })
          );

          const planForRows = subPlanIdForHeaders;
          this.updateVariantRowSaveBadges(product, planForRows);
          this.updateVariantRowPriceDisplays(product, planForRows);

          const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
          if (
            gallery &&
            typeof gallery.setSavePercent === 'function' &&
            !this.classList.contains('style-5')
          ) {
            // Use NBB's plan state when present — it is authoritative over our own
            // radio state (user may have clicked "Try once" inside norse-buy-box
            // without that being reflected in selling-plans-alt's radio inputs).
            const nbb = document.querySelector('norse-buy-box');
            const isSub = nbb
              ? nbb.isSubscriptionSelected
              : Boolean(this.querySelector(`${SELECTORS.radioInput}:checked`)?.value);
            gallery.setSavePercent(isSub ? subPct : onetimePct);
          }

          if (sellingPlanIdNum) {
            this.syncCanonicalSellingPlanInput(product, variant, sellingPlanIdNum);
          }

          const submitBtn = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          if (submitBtn) {
            if (variant.available) {
              submitBtn.removeAttribute('disabled');
            } else {
              submitBtn.setAttribute('disabled', 'disabled');
            }
          }
        } catch (error) {
          if (error?.name !== 'AbortError') console.error('Error updating prices:', error);
        }
      }
      
      updateButtonText() {
        // If a norse-buy-box element is present it manages plan state authoritatively
        // (its "Try once" / "Subscribe & Save" selection is not reflected in our own
        // radio inputs). Delegate to it so the button always shows the correct price.
        const nbb = document.querySelector('norse-buy-box');
        if (nbb && typeof nbb._updateButtonText === 'function') {
          nbb._updateButtonText();
          return;
        }

        const selectedPlan = this.querySelector(`${SELECTORS.radioInput}:checked`);
        const button = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!button) return;

        const buttonSpan = button.querySelector('span');
        if (!buttonSpan) return;

        const priceEl = selectedPlan?.value
          ? this.querySelector('[data-subscription-price]')
          : this.querySelector('[data-onetime-price]');
        const price = priceEl?.textContent || '';
        if (price) buttonSpan.textContent = `ADD TO CART - ${price}`;
      }

      /** Delegates to shared formatter (themes may reference `formatMoney` on the element). */
      formatMoney(cents) {
        return Money.formatDisplay(cents);
      }
    }
  );
}
